import type { CSSProperties } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { Guilloche } from "../components/Guilloche";
import { Icon, type IconName } from "../components/Icon";
import { Logo, LogoMark } from "../components/Logo";
import { BetaBar } from "../app/AppShell";
import { useI18n, type MessageKey } from "../lib/i18n";
import { useMe, useMoney } from "../lib/queries";
import { localized } from "../lib/seo";
import "../styles/landing.css";

/** Sample statement in the hero: one account, every kind of money movement, each with a receipt. */
const STATEMENT: Array<{ who: MessageKey; what: MessageKey; minor: number; currency: "CAD" | "XAF"; receipt: number; gold?: boolean; icon: IconName | "circle" }> = [
  { who: "landing.s.1.who", what: "landing.s.1.what", minor: 12_000, currency: "CAD", receipt: 4182, icon: "pay" },
  { who: "landing.s.2.who", what: "landing.s.2.what", minor: -65_000, currency: "XAF", receipt: 4179, icon: "send" },
  { who: "landing.s.3.who", what: "landing.s.3.what", minor: 1_200_000, currency: "CAD", receipt: 4171, gold: true, icon: "circle" },
  { who: "landing.s.4.who", what: "landing.s.4.what", minor: -25_000, currency: "CAD", receipt: 4166, icon: "heart" },
];

const RAILS = ["Visa", "Mastercard", "American Express", "Orange Money", "MTN Mobile Money", "Moov Money", "Wave", "M-Pesa"];

const PERSONAL: Array<{ icon: IconName; title: MessageKey; body: MessageKey }> = [
  { icon: "chart", title: "landing.p.plan.title", body: "landing.p.plan.body" },
  { icon: "card", title: "landing.p.card.title", body: "landing.p.card.body" },
  { icon: "send", title: "landing.p.send.title", body: "landing.p.send.body" },
  { icon: "circle", title: "landing.p.circles.title", body: "landing.p.circles.body" },
  { icon: "heart", title: "landing.p.fund.title", body: "landing.p.fund.body" },
  { icon: "globe", title: "landing.p.fx.title", body: "landing.p.fx.body" },
];

const TRUST: Array<{ title: MessageKey; body: MessageKey }> = [
  { title: "landing.t.ledger.title", body: "landing.t.ledger.body" },
  { title: "landing.t.receipts.title", body: "landing.t.receipts.body" },
  { title: "landing.t.security.title", body: "landing.t.security.body" },
  { title: "landing.t.partners.title", body: "landing.t.partners.body" },
];

export function Landing() {
  const { t, locale, setLocale } = useI18n();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // The landing page has one address per language (/ and /fr) so search engines index both.
  const onFr = pathname.startsWith("/fr");
  const L = (path: string) => localized(path, onFr ? "fr" : "en");
  const money = useMoney();
  const me = useMe();
  const signedIn = Boolean(me.data);
  const start = signedIn ? "/app" : "/signup";
  const business = signedIn ? "/app/orgs/new" : `/signup?next=${encodeURIComponent("/app/orgs/new")}`;

  return (
    <div className="landing">
      <BetaBar />
      <div className="lp-night">
        <header className="lp-nav lp-container">
          <Link to={L("/")} aria-label="CafonaPay" style={{ textDecoration: "none" }}>
            <Logo size={28} light />
          </Link>
          <nav className="lp-links" aria-label={t("landing.nav.label")}>
            <a href="#personal">{t("landing.nav.personal")}</a>
            <a href="#business">{t("landing.nav.business")}</a>
            <Link to="/developers">{t("landing.developers")}</Link>
            <a href="#trust">{t("landing.nav.trust")}</a>
          </nav>
          <div className="lp-actions">
            <button
              className="lang-toggle"
              onClick={() => {
                const next = locale === "en" ? "fr" : "en";
                setLocale(next);
                navigate(localized("/", next));
              }}
              aria-label="Language / Langue"
            >
              {locale === "en" ? "FR" : "EN"}
            </button>
            {signedIn ? (
              <Link className="btn btn-light btn-sm" to="/app">
                {t("nav.openApp")}
              </Link>
            ) : (
              <>
                <Link className="lp-signin" to="/login">
                  {t("nav.signIn")}
                </Link>
                <Link className="btn btn-light btn-sm lp-open" to="/signup">
                  {t("landing.cta.open")}
                </Link>
              </>
            )}
          </div>
        </header>

        <section className="lp-hero lp-container">
          <div className="lp-hero-copy">
            <h1>{t("landing.title")}</h1>
            <p className="lp-lede">{t("landing.lede")}</p>
            <div className="lp-ctas">
              <Link className="btn btn-gold" to={start}>
                {signedIn ? t("nav.openApp") : t("landing.cta.openFree")}
              </Link>
              <Link className="btn btn-outline-light" to={business}>
                {t("landing.cta.business")}
              </Link>
            </div>
            <p className="lp-fine">{t("landing.hero.fine")}</p>
          </div>

          <figure className="lp-statement" aria-label={t("landing.s.label")}>
            <Guilloche className="lp-rosette" opacity={0.13} />
            <div className="lp-card">
              <div className="lp-card-balance">
                <Guilloche className="lp-card-rosette" opacity={0.2} />
                <span className="lp-card-label">{t("landing.s.balance")}</span>
                <span className="lp-card-amount num">{money(1_220_500, "CAD")}</span>
                <span className="lp-card-others num">
                  <span>{money(15_000_000, "XAF")}</span>
                  <span>{money(42_000, "USD")}</span>
                </span>
              </div>
              <ul className="lp-rows">
                {STATEMENT.map((r, i) => (
                  <li key={r.receipt} style={{ "--i": i } as CSSProperties}>
                    <span className={`lp-row-icon${r.gold ? " is-gold" : ""}`}>
                      <Icon name={r.icon} size={18} />
                    </span>
                    <span className="lp-row-main">
                      <span className="lp-row-who">{t(r.who)}</span>
                      <span className="lp-row-what">
                        {t(r.what)} · <span className="lp-row-receipt">{t("landing.s.receipt", { n: r.receipt.toLocaleString(locale === "fr" ? "fr-CA" : "en-CA") })}</span>
                      </span>
                    </span>
                    <span className={`lp-row-amount num${r.minor > 0 ? " is-in" : ""}${r.gold ? " is-gold" : ""}`}>
                      {r.minor > 0 ? "+" : "−"}
                      {money(Math.abs(r.minor), r.currency)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <figcaption className="lp-verified">
              <span className="lp-verified-mark" aria-hidden>
                <Icon name="check" size={16} />
              </span>
              <span>
                <strong>{t("landing.s.verified")}</strong>
                <span className="lp-hash">sha256 3e1f2b74…a6c2</span>
              </span>
            </figcaption>
          </figure>
        </section>

        <div className="lp-rails lp-container">
          <span>{t("landing.rails")}</span>
          <ul>
            {RAILS.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </div>
      </div>

      <main>
        <section id="personal" className="lp-section lp-container">
          <div className="lp-section-head">
            <h2>{t("landing.p.title")}</h2>
            <p>{t("landing.p.lede")}</p>
          </div>
          <div className="lp-grid">
            {PERSONAL.map((f) => (
              <article key={f.title} className="lp-feature">
                <span className="lp-feature-icon">
                  <Icon name={f.icon} />
                </span>
                <h3>{t(f.title)}</h3>
                <p>{t(f.body)}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="business" className="lp-band">
          <div className="lp-container lp-split">
            <div className="lp-split-copy">
              <h2>{t("landing.b.title")}</h2>
              <p className="lp-section-lede">{t("landing.b.lede")}</p>
              <ul className="lp-checks">
                {(["landing.b.1", "landing.b.2", "landing.b.3", "landing.b.4"] as MessageKey[]).map((k) => (
                  <li key={k}>
                    <Icon name="check" size={18} />
                    {t(k)}
                  </li>
                ))}
              </ul>
              <div className="lp-ctas">
                <Link className="btn btn-primary" to={business}>
                  {t("landing.cta.business")}
                </Link>
                <Link className="btn" to="/developers">
                  {t("landing.b.docs")}
                </Link>
              </div>
            </div>

            <div className="lp-checkout" aria-label={t("landing.b.mock")} role="img">
              <div className="lp-co-merchant">
                <span className="lp-co-logo">AC</span>
                <span>
                  <strong>{t("landing.b.mock.org")}</strong>
                  <span className="muted">{t("landing.b.mock.item")}</span>
                </span>
              </div>
              <div className="lp-co-amount num">{money(12_000, "CAD")}</div>
              <div className="lp-co-tabs">
                <span className="is-on">{t("landing.b.mock.card")}</span>
                <span>{t("landing.b.mock.mobile")}</span>
                <span>{t("landing.b.mock.wallet")}</span>
              </div>
              <div className="lp-co-field">
                <span>4242 4242 4242 4242</span>
                <span className="muted">12 / 29</span>
              </div>
              <div className="lp-co-pay">{t("landing.b.mock.pay", { amount: money(12_000, "CAD") })}</div>
              <p className="lp-co-fine muted">{t("landing.b.mock.fine")}</p>
            </div>
          </div>

          <div className="lp-container lp-dev">
            <div>
              <h3>{t("landing.d.title")}</h3>
              <p className="muted">{t("landing.d.body")}</p>
            </div>
            <pre className="lp-code" aria-label={t("landing.d.code")}>
              <code>
                <span className="c-k">curl</span> https://cafonapay.com/api/v1/checkout/sessions \{"\n"}
                {"  "}-H <span className="c-s">"Authorization: Bearer sk_test_…"</span> \{"\n"}
                {"  "}-d <span className="c-s">'{"{"}"amountMinor": 12000, "currency": "CAD"{"}"}'</span>
                {"\n\n"}
                <span className="c-c"># → {"{"} "url": "https://cafonapay.com/checkout/pay_…" {"}"}</span>
              </code>
            </pre>
          </div>
        </section>

        <section id="trust" className="lp-section lp-container lp-trust">
          <div className="lp-section-head">
            <h2>{t("landing.t.title")}</h2>
            <p>{t("landing.t.lede")}</p>
          </div>
          <div className="lp-trust-grid">
            <div className="lp-receipt" aria-hidden>
              <Guilloche className="lp-receipt-rosette" opacity={0.5} />
              <div className="lp-receipt-head">
                <LogoMark size={26} />
                <span className="lp-receipt-badge">
                  <Icon name="check" size={14} /> {t("verify.valid")}
                </span>
              </div>
              <span className="lp-receipt-kicker">{t("verify.kicker")}</span>
              <span className="lp-receipt-amount num">{money(1_200_000, "CAD")}</span>
              <dl>
                <div>
                  <dt>{t("cert.from")}</dt>
                  <dd>{t("landing.r.from")}</dd>
                </div>
                <div>
                  <dt>{t("cert.to")}</dt>
                  <dd>Ama K.</dd>
                </div>
                <div>
                  <dt>{t("verify.number")}</dt>
                  <dd className="num">#4,171</dd>
                </div>
              </dl>
              <span className="lp-receipt-hash">3e1f2b745d9855f6d5da66d85cc48fa7029f03b6107d1fe655eae3d95a3ba6c2</span>
            </div>
            <ul className="lp-facts">
              {TRUST.map((f) => (
                <li key={f.title}>
                  <h3>{t(f.title)}</h3>
                  <p>{t(f.body)}</p>
                </li>
              ))}
              <li className="lp-facts-link">
                <Link to={L("/verify")}>{t("landing.t.verify")}</Link>
              </li>
            </ul>
          </div>
        </section>

        <section className="lp-cta-band">
          <Guilloche className="lp-cta-rosette" opacity={0.18} />
          <div className="lp-container lp-cta-inner">
            <h2>{t("landing.final.title")}</h2>
            <p>{t("landing.final.body")}</p>
            <div className="lp-ctas">
              <Link className="btn btn-gold" to={start}>
                {signedIn ? t("nav.openApp") : t("landing.cta.openFree")}
              </Link>
              <Link className="btn btn-outline-light" to={business}>
                {t("landing.cta.business")}
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="lp-footer lp-container">
        <div className="lp-footer-brand">
          <Logo size={24} />
          <p className="muted">{t("landing.footer")}</p>
        </div>
        <nav className="lp-footer-cols" aria-label={t("landing.footer.nav")}>
          <div>
            <h3>{t("landing.f.product")}</h3>
            <a href="#personal">{t("landing.nav.personal")}</a>
            <a href="#business">{t("landing.nav.business")}</a>
            <Link to={signedIn ? "/app/circles" : "/signup?next=/app/circles"}>{t("nav.circles")}</Link>
            <Link to={signedIn ? "/app/campaigns/new" : "/signup?next=/app/campaigns/new"}>{t("nav.campaigns")}</Link>
          </div>
          <div>
            <h3>{t("landing.f.trust")}</h3>
            <Link to={L("/verify")}>{t("verify.title")}</Link>
            <Link to="/developers">{t("landing.developers")}</Link>
          </div>
          <div>
            <h3>{t("landing.f.legal")}</h3>
            <Link to={L("/legal/terms")}>{t("legal.terms")}</Link>
            <Link to={L("/legal/privacy")}>{t("legal.privacy")}</Link>
          </div>
        </nav>
        <p className="lp-copyright muted">© {new Date().getFullYear()} CafonaPay</p>
      </footer>
    </div>
  );
}
